<template>
	<div class="typing-title">
		<h1 class="gradient-title">
			<span v-html="animatedTitle"></span>
		</h1>
	</div>
</template>

<script>
	export default {
		name: 'TypingTitle',
		props: {
			title: {
				type: String,
				required: true,
			},
			typingSpeed: {
				type: Number,
				default: 35, // milliseconds per character (30% faster than 50ms)
			},
			startDelay: {
				type: Number,
				default: 500, // milliseconds before starting
			},
			animationId: {
				type: String,
				default: null, // unique identifier for this animation
			},
		},
		data() {
			return {
				typingIndex: 0,
				isTyping: false,
				fullTitle: this.title,
				hasCompleted: false,
			}
		},
		computed: {
			animatedTitle() {
				const text = this.fullTitle.substring(0, this.typingIndex)
				return this.wrapEmojisInSpans(text)
			},
			storageKey() {
				return this.animationId ? `typing-animation.${this.animationId}.completed` : null
			},
		},
		watch: {
			title: {
				handler(newTitle) {
					this.fullTitle = newTitle
					this.checkAndStartAnimation()
				},
				immediate: true,
			},
		},
		methods: {
			wrapEmojisInSpans(text) {
				// Emoji regex pattern - matches most emojis including skin tone modifiers
				const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE00}-\u{FE0F}]|[\u{1F3FB}-\u{1F3FF}]/gu

				return text.replace(emojiRegex, emoji => `<span class="emoji">${emoji}</span>`)
			},
			checkAndStartAnimation() {
				// If no animation ID is provided, always animate
				if (!this.animationId) {
					this.startTypingAnimation()
					return
				}

				// Check if this animation has already been completed
				const hasCompleted = sessionStorage.getItem(this.storageKey) === 'true'

				if (hasCompleted) {
					// Show the full title immediately without animation
					this.typingIndex = this.fullTitle.length
					this.hasCompleted = true
					// Emit the finished event immediately
					this.$emit('typing-finished')
				} else {
					// Start the animation
					this.startTypingAnimation()
				}
			},
			startTypingAnimation() {
				if (this.isTyping) return

				this.isTyping = true
				this.typingIndex = 0

				const typeNextChar = () => {
					if (this.typingIndex < this.fullTitle.length) {
						this.typingIndex++
						setTimeout(typeNextChar, this.typingSpeed)
					} else {
						this.isTyping = false
						this.hasCompleted = true

						// Mark as completed in session storage
						if (this.animationId) {
							sessionStorage.setItem(this.storageKey, 'true')
						}

						// Emit event when typing is finished
						this.$emit('typing-finished')
					}
				}

				// Start the animation after the specified delay
				setTimeout(typeNextChar, this.startDelay)
			},
			resetAnimation() {
				this.typingIndex = 0
				this.isTyping = false
				this.hasCompleted = false
			},
			restartAnimation() {
				this.resetAnimation()
				// Clear the session storage entry if it exists
				if (this.animationId) {
					sessionStorage.removeItem(this.storageKey)
				}
				this.startTypingAnimation()
			},
		},
		mounted() {
			this.checkAndStartAnimation()
		},
	}
</script>

<style lang="scss" scoped>
	.typing-title {
		text-align: left;
		margin-bottom: unset;
	}
</style>

<style lang="scss">
	.gradient-title {
		background: linear-gradient(150deg, #00106d 0%, #9355ff 80%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		font-family: 'Plus Jakarta Sans', sans-serif;
		font-size: 24px;
		font-weight: 700;
		font-style: bold;
		line-height: 132%;
		letter-spacing: -0.5px;
		margin: 0;

		.emoji {
			-webkit-text-fill-color: initial;
			background: none;
			background-clip: none;
		}
	}
</style>
